import toast from 'react-hot-toast';
import instance from '../../services/baseService'; // Adjust the import path as needed



export const getStatusDisplay = (status) => {
    const statusMap = {
        1: {
            label: "Success",
            className: "bg-green-100 text-green-700 px-3 py-1 rounded-full font-bold"
        },
        2: {
            label: "Processing",
            className: "bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-bold"
        },
        3: {
            label: "Failed",
            className: "bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold"
        },
        4: {
            label: "Validation Error",
            className: "bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold"
        },
        5: {
            label: "Processing",
            className: "bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-bold"
        },
        6: {
            label: "Failed",
            className: "bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold"
        },
        7: {
            label: "Try Again",
            className: "bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold"
        }

    };

    const statusInfo = statusMap[status];
    return statusInfo ? (
        <span className={statusInfo.className}>{statusInfo.label}</span>
    ) : (
        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-sm font-bold">
            Unknown
        </span>
    );
};


export const getPreviousStatusVariant = (status) => {
    switch (status) {
        case "Previous Period Matched Data":
            return "success";   // Success
        case "Previous Period Almost Matching":
            return "warning";   // Processing
        // case 3:
        //   return "danger";    // Failed
        default:
            return "secondary"; // Fallback
    }
};


export const downloadErrorFile = async (clientId, fileId, fileName = null) => {
    // Input validation
    if (!clientId || !fileId) {
        toast.error('Client ID and File ID are required for download');
        return false;
    }

    // Show loading toast
    const loadingToast = toast.loading('Preparing download...');

    try {
        // Make API call to get presigned URL
        const response = await instance.get(
            `/api/v1/reconciliation/download/error/${clientId}/${fileId}`
        );

        // Check if response is successful
        if (response.data && response.data.type === 'success') {
            const { presigned_url } = response.data.data;

            if (!presigned_url) {
                throw new Error('Download URL not found in response');
            }

            // Dismiss loading toast
            toast.dismiss(loadingToast);

            // Trigger download
            triggerDownload(presigned_url, fileName);

            // Show success toast
            toast.success('Download started successfully');
            return true;

        } else {
            throw new Error(response.data?.message || 'Failed to get download URL');
        }

    } catch (error) {
        console.error('Download Error File Error:', error);

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        // Handle different error scenarios
        let errorMessage = 'Failed to download error file. Please try again.';

        if (error.response) {
            const { status, data } = error.response;

            switch (status) {
                case 404:
                    errorMessage = 'Error file not found. It may have been deleted or does not exist.';
                    break;
                case 403:
                    errorMessage = 'Access denied. You don\'t have permission to download this file.';
                    break;
                case 400:
                    errorMessage = 'Invalid request. Please check the file details and try again.';
                    break;
                case 500:
                case 502:
                case 503:
                    errorMessage = 'Server error occurred. Please try again later.';
                    break;
                default:
                    errorMessage = data?.message || errorMessage;
            }
        } else if (error.request) {
            errorMessage = 'Network error. Please check your connection and try again.';
        } else {
            errorMessage = error.message || errorMessage;
        }

        // Show error toast
        toast.error(errorMessage);
        return false;
    }
};

export const retryFileProcess = async (clientId, fileId) => {
    try {
        const response = await instance.get(
            `/api/v1/reconciliation/retry/file/${clientId}/${fileId}`
        );

        if (response.data.type === "success") {
            toast.success(response.data.message || "File started to processing");
        } else {
            toast.error(response.data.message || "Failed to retry file process");
        }

        return response.data;
    } catch (err) {
        console.error("Error retrying file process:", err);
        const errorMessage =
            err.response?.data?.message ||
            "Failed to retry file process. Please try again.";
        toast.error(errorMessage);
        return {
            type: "error",
            message: errorMessage,
            data: err.response?.data?.data || {},
        };
    }
};



const triggerDownload = (presignedUrl, fileName = null) => {
    try {
        const link = document.createElement('a');
        link.href = presignedUrl;

        if (fileName) {
            link.download = fileName;
        } else {
            const urlParts = presignedUrl.split('/');
            const urlFileName = urlParts[urlParts.length - 1].split('?')[0];
            if (urlFileName) {
                link.download = urlFileName;
            }
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

    } catch (error) {
        console.error('Trigger Download Error:', error);
        window.open(presignedUrl, '_blank');
        toast.success('Download opened in new tab');
    }
};

export const calculateMetricsPercentages = (metrics) => {
    const { matched, partial, missing } = metrics;
    const total = matched + partial + missing;

    if (total === 0) {
        return {
            matchedPercent: 0,
            partialPercent: 0,
            missingPercent: 0
        };
    }

    return {
        matchedPercent: Math.round((matched / total) * 100),
        partialPercent: Math.round((partial / total) * 100),
        missingPercent: Math.round((missing / total) * 100)
    };
};

export const mapUploadTrendsWithVariants = (uploadTrends) => {
    return uploadTrends.map(item => {
        let variant = 'secondary';
        const lowerLabel = item.label.toLowerCase();

        if (lowerLabel.includes('completed') || lowerLabel.includes('success')) {
            variant = 'success';
        } else if (lowerLabel.includes('processing') || lowerLabel.includes('pending')) {
            variant = 'warning';
        } else if (lowerLabel.includes('error') || lowerLabel.includes('failed') || lowerLabel.includes('validation') || lowerLabel.includes('try again')) {
        variant = 'danger';
        }

        return {
            ...item,
            variant
        };
    });
};


export const transformApiData = (apiData) => {
    return apiData.map(item => {
        // Calculate differences manually
        const rosctelDiff = (item.rosctelAmount1 || 0) - (item.rosctelAmount2 || 0);
        const rodtepDiff = (item.rodTepAmount1 || 0) - (item.rodTepAmount2 || 0);

        // Map status from API to component status
        let status = 'MATCHED';
        let remarks = 'Matched Data';

        switch (item.status) {
            case 'MATCHED':
                status = 'MATCHED';
                remarks = item.status_code || 'Matched Data';
                break;
            case 'ALMOST_MATCH':
                status = 'ALMOST_MATCH';
                remarks = item.status_code || 'Almost Matching';
                break;
            case 'MISSING_IN_ICEGATE':
                status = 'MISSING_IN_ICEGATE';
                remarks = item.status_code || 'Missing in ICEGATE';
                break;
            case 'MISSING_IN_STATEMENT3':
                status = 'MISSING_IN_STATEMENT3';
                remarks = item.status_code || 'Missing in Statement 3';
                break;
            default:
                status = 'MATCHED';
                remarks = item.status_code || 'Matched Data';
        }

        return {
            id: item.id,
            remarks: remarks,
            from_previous: item.from_previous,
            sbNo1: item.sbNo1,
            sbDate1: item.sbDate1,
            portCode1: item.portCode1,
            rosctelAmount1: item.rosctelAmount1,
            rodTepAmount1: item.rodTepAmount1,
            sbNo2: item.sbNo2,
            sbDate2: item.sbDate2,
            portCode2: item.portCode2,
            rosctelAmount2: item.rosctelAmount2,
            rodTepAmount2: item.rodTepAmount2,
            rosctelDiff: rosctelDiff,
            rodtepDiff: rodtepDiff,
            status: status
        };
    });
};


export const reportHeaders = {
    mainSections: [
        { title: "", colspan: 1 },
        { title: "Statement 3 Data", colspan: 5 },
        { title: "ICEGATE data", colspan: 5 },
        { title: "Difference", colspan: 3 }
    ],
    columns: [
        { key: "remarks", title: "Remarks", section: "info" },
        { key: "sbNo1", title: "SB No", section: "statement3" },
        { key: "sbDate1", title: "SB Date", section: "statement3" },
        { key: "portCode1", title: "Port Code", section: "statement3" },
        { key: "rosctelAmount1", title: "Rosctel Amount", section: "statement3" },
        { key: "rodTepAmount1", title: "RodTep Amount", section: "statement3" },
        { key: "sbNo2", title: "SB No", section: "icegate" },
        { key: "sbDate2", title: "SB Date", section: "icegate" },
        { key: "portCode2", title: "Port Code", section: "icegate" },
        { key: "rosctelAmount2", title: "Rosctel Amount", section: "icegate" },
        { key: "rodTepAmount2", title: "RodTep Amount", section: "icegate" },
        { key: "rosctelDiff", title: "Rosctel Amount", section: "difference" },
        { key: "rodtepDiff", title: "RodTep Amount", section: "difference" }
    ]
};